import * as htmlToImage from 'html-to-image';
import { planPdfLayout } from './planPdfLayout';

const PADDING = 10;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

// Cuts out the band a placement asks for. sourceTop/sourceHeight are in the
// element's own CSS px, while the capture is scaled by the device pixel ratio,
// so they have to be converted before cropping.
const cropBand = (image, elementHeight, sourceTop, sourceHeight) => {
  const pxPerCssPx = image.height / elementHeight;
  const top = Math.round(sourceTop * pxPerCssPx);
  const height = Math.min(
    Math.round(sourceHeight * pxPerCssPx),
    image.height - top,
  );

  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = height;
  canvas
    .getContext('2d')
    .drawImage(image, 0, top, image.width, height, 0, 0, image.width, height);

  return canvas.toDataURL('image/png');
};

export async function createPdf(doc, elements) {
  const sections = [];

  for (let i = 0; i < elements.length; i++) {
    const el = elements.item(i);
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    // a section that is not on screen would only contribute a blank image
    if (!width || !height) {
      sections.push(null);
      continue;
    }

    sections.push({
      width,
      height,
      data: await htmlToImage.toPng(el, {
        skipFonts: true,
        // controls that live inside a captured region stay out of the report
        filter: (node) => !node.classList?.contains('no-print'),
      }),
    });
  }

  const placements = planPdfLayout(
    sections.map((section) => section ?? { width: 0, height: 0 }),
    {
      pageWidth: doc.internal.pageSize.getWidth(),
      pageHeight: doc.internal.pageSize.getHeight(),
      padding: PADDING,
    },
  );

  const loaded = new Map();
  let page = 0;

  for (const placement of placements) {
    while (page < placement.page) {
      doc.addPage();
      page += 1;
    }

    const section = sections[placement.index];
    let data = section.data;

    // only a section split across pages needs cutting up
    if (placement.sourceHeight < section.height) {
      if (!loaded.has(placement.index)) {
        loaded.set(placement.index, await loadImage(section.data));
      }

      data = cropBand(
        loaded.get(placement.index),
        section.height,
        placement.sourceTop,
        placement.sourceHeight,
      );
    }

    doc.addImage(
      data,
      'PNG',
      placement.x,
      placement.y,
      placement.width,
      placement.height,
      `image${placement.index}-${placement.part}`,
    );
  }
}
